/**
 * Pure helpers extracted from the ~180-line stepper-building algorithm
 * that used to live in 5 detail views.
 *
 * Each function corresponds to one step in the algorithm. The composable
 * `use-ticket-stepper.js` glues them together with reactive state; views
 * call the composable instead of inlining the algorithm.
 *
 * Domain glossary (CONTEXT.md):
 *   CREADO=1, EN CORREDORA=7, EN ASEGURADORA=8, EJECUTADO=3, CANCELADO=4
 *
 * The backend exposes state transitions as `state.tag` strings
 * (e.g. 'En aseguradora'). The stepper component reads step keys
 * ('creado', 'revision_bewell', 'revision_aseguradora', 'ejecutado',
 * 'cancelado'). `getStateType` is the one-to-one map between them.
 */

import parseDate from '../utils/dateFormatter';

export const STATE_TYPE_MAP = Object.freeze({
  Creado: 'creado',
  'En corredora': 'revision_bewell',
  'En aseguradora': 'revision_aseguradora',
  Ejecutado: 'ejecutado',
  Cancelado: 'cancelado'
});

export const getStateType = (state) => STATE_TYPE_MAP[state];

const sortByIdDesc = (states) => [...states].sort((a, b) => b.id - a.id);
const sortByIdAsc = (states) => [...states].sort((a, b) => a.id - b.id);

export const getLastStateTag = (states) => sortByIdDesc(states)[0]?.state?.tag;

export const getSecondLastStateTag = (states) =>
  sortByIdDesc(states)[1]?.state?.tag;

export const getLastStateId = (states) =>
  states.length === 0 ? '' : sortByIdDesc(states)[0].state.id;
// State arrays can be reordered by consumers before the header is rendered;
// use the newest state id rather than relying on array order.

export const computeCurrentStep = (states) => {
  const lastTag = getLastStateTag(states);
  let step = getStateType(lastTag);
  if (step === 'cancelado') {
    // Ant Design: an X on the stepper means "rejected at this step".
    // We move the X to the last good state (the one right before the
    // cancel transition).
    step = getStateType(getSecondLastStateTag(states));
  }
  return step;
};

export const buildRecordsSteps = (states) =>
  states.map((s) => ({ title: s.state.tag, description: s.createdAt }));

export const buildFinalObj = (states) => {
  const sorted = sortByIdAsc(states);
  const finalObj = {};
  for (const element of sorted) {
    const stateName = getStateType(element.state.tag);
    finalObj[stateName] = element.createdAt;
  }
  // If the response never went through the Creado state explicitly, the
  // 'creado' key would be missing. Fall back to the first known state's
  // createdAt so the stepper still shows a creation date.
  if (!finalObj.creado && sorted.length > 0) {
    finalObj.creado = sorted[0].createdAt;
  }
  return finalObj;
};

export const removeFutureStates = (finalObj, step) => {
  // If the ticket rolled back to an earlier state (e.g. En aseguradora
  // → En corredora), there can be stale future-step dates in the map.
  // Drop them so the stepper doesn't render a step the ticket hasn't
  // actually reached.
  switch (step) {
    case 'creado':
      delete finalObj.revision_bewell;
      delete finalObj.revision_aseguradora;
      delete finalObj.ejecutado;
      break;
    case 'revision_bewell':
      delete finalObj.revision_aseguradora;
      delete finalObj.ejecutado;
      break;
    case 'revision_aseguradora':
      delete finalObj.ejecutado;
      break;
    default:
      break;
  }
};

export const moveCancelToPenultimate = (finalObj, lastStateTag) => {
  if (lastStateTag !== 'Cancelado') return finalObj;
  const keys = Object.keys(finalObj);
  if (keys.length < 2) return finalObj;
  // The cancel happened at the previous step, so we display the cancel
  // date on the previous step's slot and remove the dedicated 'cancelado'
  // entry — the X marker comes from the stepper component's status.
  finalObj[keys[keys.length - 2]] = finalObj[keys[keys.length - 1]];
  delete finalObj.cancelado;
  return finalObj;
};

export const formatDates = (finalObj) => {
  const out = {};
  for (const k of Object.keys(finalObj)) {
    out[k] = parseDate(finalObj[k]);
  }
  return out;
};

export const buildHeaderStateData = (lastStateTag) => {
  // Returns a fresh plain object. The composable merges it onto a
  // reactive() so views can bind to either set of fields they use
  // (some views read showCancelAndExecuted, others cancel_disabled).
  const header = {
    text: '',
    color: 'default',
    cancel_disabled: true,
    revisado_disabled: true,
    show_buttons: false,
    showCancelAndExecuted: false
  };
  switch (lastStateTag) {
    case 'Creado':
      header.text = 'Creado';
      header.color = 'blue';
      header.showCancelAndExecuted = true;
      break;
    case 'En corredora':
      header.text = 'En revisión corredora';
      header.color = 'gold';
      header.show_buttons = true;
      header.revisado_disabled = false;
      break;
    case 'En aseguradora':
      header.text = 'En revisión aseguradora';
      header.color = 'gold';
      header.showCancelAndExecuted = true;
      header.show_buttons = true;
      break;
    case 'Ejecutado':
      header.text = 'Ejecutado';
      header.color = 'green';
      break;
    case 'Cancelado':
      header.text = 'Cancelado';
      header.color = 'red';
      break;
    default:
      header.text = lastStateTag || '';
      header.color = 'default';
      break;
  }
  return header;
};
